import React, { useState, useEffect, useContext, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiSave, FiX, FiPlus, FiTrash2, FiFileText, FiPackage,
  FiCreditCard, FiArrowLeft, FiCheckCircle, FiAlertCircle,
  FiEdit3, FiAnchor, FiTruck,
  FiInfo, FiDollarSign, FiHash, FiGlobe, FiLock,
  FiUploadCloud, FiDownload, FiFile, FiImage, FiPaperclip,
  FiSearch, FiUserPlus,
  FiChevronUp, FiChevronDown, FiMaximize2, FiCheck
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { documentAPI, proformaAPI, companyAPI, attachmentAPI, contactAPI, buyerDetailAPI, settingsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

// ─── Helpers ────────────────────────────────────────────────────────────────────
const toWords = (num) => {
  if (!num || isNaN(num)) return '';
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
    'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const convert = (n) => {
    if (n === 0) return '';
    if (n < 20) return ones[n] + ' ';
    if (n < 100) return tens[Math.floor(n / 10)] + ' ' + ones[n % 10] + ' ';
    if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred ' + convert(n % 100);
    if (n < 100000) return convert(Math.floor(n / 1000)) + 'Thousand ' + convert(n % 1000);
    if (n < 10000000) return convert(Math.floor(n / 100000)) + 'Lakh ' + convert(n % 100000);
    return convert(Math.floor(n / 10000000)) + 'Crore ' + convert(n % 10000000);
  };

  const intPart = Math.floor(Math.abs(num));
  const decPart = Math.round((Math.abs(num) - intPart) * 100);
  let result = 'USD ' + convert(intPart).trim();
  if (decPart > 0) result += ' and Cents ' + convert(decPart).trim();
  result += ' Only';
  return result;
};

const fmt = (v) =>
  v != null && v !== '' && !isNaN(v)
    ? Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '';

const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return isNaN(dt) ? '' : dt.toISOString().slice(0, 10);
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const getFileIcon = (mimeType) => {
  if (!mimeType) return FiFile;
  if (mimeType.startsWith('image/')) return FiImage;
  return FiFile;
};

const SHIPMENT_MODES = ['Sea', 'Air', 'By Road / By Truck', 'Rail', 'Multimodal'];
// Documents saved before the rename stored 'Road'
const normalizeShipmentMode = (m) => (m === 'Road' ? 'By Road / By Truck' : m);
const TABS = [
  { key: 'invoice', label: 'Commercial Invoice', icon: FiFileText },
  { key: 'iec',     label: 'IEC Display',         icon: FiCreditCard },
];

let _lineKeyId = 0;
const nextLineKey = () => ++_lineKeyId;

const EMPTY_LINE_ITEM = {
  _key: 0,
  marks_and_nos: '',
  hs_code: '',
  description_of_goods: '',
  quantity: '',
  uom: '',
  rate_usd: '',
  amount_usd: 0,
  no_of_packages: '',
  package_type: '',
  net_weight_kg: '',
  gross_weight_kg: '',
};

const initialFormState = {
  // Document type (always commercial invoice)
  document_type: 'COMMERCIAL INVOICE',

  // Auto-populated from company (read-only)
  exporter_name: '',
  exporter_address: '',
  iec: '',

  // Buyer
  buyer_name: '',
  buyer_address: '',
  buyer_details: '',
  buyer_email: '',
  buyer_phone: '',
  // Shipment
  mode_of_shipment: 'Sea',
  shipment_from: '',
  shipment_to: '',
  terms_of_delivery: '',
  mode_of_payment: '',

  // L/C
  lc_no: '',
  lc_amendment_ref: '',
  lc_amendment_ref_date: '',
  lc_opening_date: new Date().toISOString().slice(0, 10),
  lc_opening_bank: '',
  lc_opening_bank_address: '',
  lc_value_usd: '',

  // Insurance
  insurance_ref: '',
  insurance_company: '',

  // Customs
  hs_code: '',
  total_packages: '',
  total_net_weight: '',
  total_gross_weight: '',
  total_package_unit: '',
  other_ref: '',
  other_ref_2: '',
  declaration: '',

  // Invoice value break-up (printed under goods description on PDF)
  fob_value_usd: '',
  freight_charges_usd: '',

  // Auto-calculated
  total_usd: 0,
  total_in_words: '',

  // TR / BOE / Despatch
  despatch_doc_no: '',
  despatch_date: '',
  carrier: '',
  vehicle_no: '',
  sb_no: '',
  sb_date: '',
  export_date: '',
  to_the_order_of: '',
  notify: '',
  drafts_drawn: '',

  // Post-creation
  bill_ref: '',
  due_date: '',
  payment_date: '',
  fob_inr: '',
  drawback: '',
  commodity: '',

  // Invoice No (auto-generated or manual)
  invoice_no: '',

  // Document date (auto-filled with today's date)
  document_date: new Date().toISOString().slice(0, 10),

  // Status
  status: 'draft',

  // Proforma link (for commercial invoices)
  proforma_id: null,

  // Buyer detail link
  buyer_detail_id: null,
};

// ─── Styles ─────────────────────────────────────────────────────────────────────
const s = {
  page: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    maxWidth: 1200,
    margin: '0 auto',
  },
  // Header
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
    flexWrap: 'wrap',
    gap: 12,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
  },
  backBtn: {
    width: 40,
    height: 40,
    borderRadius: 12,
    border: '2px solid rgba(16,185,129,0.2)',
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#047857',
    transition: 'all 0.15s ease',
  },
  titleIcon: {
    width: 48,
    height: 48,
    borderRadius: 14,
    background: 'linear-gradient(135deg, #059669, #10b981)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
  },
  title: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: 24,
    fontWeight: 700,
    color: '#064e3b',
    margin: 0,
  },
  subtitle: {
    fontSize: 13,
    color: '#6b7280',
    marginTop: 2,
  },
  // Status Badge
  statusBadge: (status) => {
    const map = {
      draft:     { bg: '#fef3c7', color: '#92400e', label: 'Draft' },
      saved:     { bg: '#dbeafe', color: '#1e40af', label: 'Saved' },
      finalized: { bg: '#d1fae5', color: '#065f46', label: 'Finalized' },
    };
    const cfg = map[status] || map.draft;
    return {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '5px 14px',
      borderRadius: 9999,
      fontSize: 12,
      fontWeight: 700,
      background: cfg.bg,
      color: cfg.color,
      letterSpacing: 0.3,
      textTransform: 'uppercase',
    };
  },
  statusLabel: (status) => {
    const map = {
      draft: 'Draft', saved: 'Saved', finalized: 'Finalized',
    };
    return map[status] || 'Draft';
  },
  // Tabs
  tabBar: {
    display: 'flex',
    gap: 4,
    background: 'rgba(255,255,255,0.7)',
    backdropFilter: 'blur(12px)',
    borderRadius: 14,
    padding: 4,
    marginBottom: 20,
    border: '1px solid rgba(16,185,129,0.1)',
    boxShadow: '0 2px 8px rgba(6,78,59,0.06)',
  },
  tab: (active) => ({
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '12px 18px',
    borderRadius: 11,
    border: 'none',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: active ? 700 : 500,
    fontFamily: "'Inter', sans-serif",
    color: active ? '#fff' : '#047857',
    background: active ? 'linear-gradient(135deg, #059669, #10b981)' : 'transparent',
    boxShadow: active ? '0 4px 12px rgba(16,185,129,0.3)' : 'none',
    transition: 'all 0.25s ease',
    whiteSpace: 'nowrap',
  }),
  // Card
  card: {
    background: 'rgba(255, 255, 255, 0.88)',
    backdropFilter: 'blur(16px)',
    border: '1px solid rgba(16, 185, 129, 0.1)',
    borderRadius: 18,
    padding: '28px 28px 20px',
    marginBottom: 20,
    boxShadow: '0 4px 20px rgba(6, 78, 59, 0.08)',
  },
  cardTitle: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: 16,
    fontWeight: 700,
    color: '#064e3b',
    marginBottom: 20,
    paddingBottom: 12,
    borderBottom: '2px solid rgba(16,185,129,0.12)',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  cardTitleIcon: {
    width: 32,
    height: 32,
    borderRadius: 9,
    background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#059669',
  },
  // Grid
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  grid3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 },
  // Square icon buttons beside the typed package unit, sized to the input
  pkgUnitBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 38, flex: '0 0 38px', borderRadius: 8, cursor: 'pointer',
    border: '1px solid rgba(16,185,129,0.3)', background: '#fff', color: '#047857',
  },
  grid4: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 },
  // Form controls
  formGroup: { marginBottom: 0 },
  label: {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: '#047857',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    border: '2px solid rgba(16, 185, 129, 0.18)',
    borderRadius: 9,
    fontSize: 14,
    outline: 'none',
    fontFamily: "'Inter', sans-serif",
    color: '#1e293b',
    background: '#fff',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    boxSizing: 'border-box',
  },
  inputReadOnly: {
    width: '100%',
    padding: '10px 14px',
    border: '2px solid rgba(16, 185, 129, 0.1)',
    borderRadius: 9,
    fontSize: 14,
    fontFamily: "'Inter', sans-serif",
    color: '#64748b',
    background: '#f8fafc',
    boxSizing: 'border-box',
    cursor: 'default',
  },
  textarea: {
    width: '100%',
    padding: '10px 14px',
    border: '2px solid rgba(16, 185, 129, 0.18)',
    borderRadius: 9,
    fontSize: 14,
    outline: 'none',
    fontFamily: "'Inter', sans-serif",
    color: '#1e293b',
    background: '#fff',
    resize: 'vertical',
    minHeight: 72,
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '10px 14px',
    border: '2px solid rgba(16, 185, 129, 0.18)',
    borderRadius: 9,
    fontSize: 14,
    outline: 'none',
    fontFamily: "'Inter', sans-serif",
    color: '#1e293b',
    background: '#fff',
    appearance: 'none',
    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%23047857' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10l-5 5z'/%3E%3C/svg%3E\")",
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    paddingRight: 36,
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    boxSizing: 'border-box',
    cursor: 'pointer',
  },
  // Line items table
  lineTable: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: 12,
  },
  lineTh: {
    padding: '10px 12px',
    background: 'linear-gradient(135deg, #064e3b, #047857)',
    color: '#fff',
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    textAlign: 'left',
    whiteSpace: 'nowrap',
  },
  lineTd: {
    padding: '8px 8px',
    borderBottom: '1px solid rgba(16,185,129,0.1)',
    verticalAlign: 'top',
  },
  lineInput: {
    width: '100%',
    padding: '8px 10px',
    border: '1.5px solid rgba(16, 185, 129, 0.15)',
    borderRadius: 7,
    fontSize: 13,
    outline: 'none',
    fontFamily: "'Inter', sans-serif",
    color: '#1e293b',
    background: '#fff',
    transition: 'border-color 0.15s ease',
    boxSizing: 'border-box',
  },
  lineInputSmall: {
    width: '100%',
    padding: '8px 10px',
    border: '1.5px solid rgba(16, 185, 129, 0.15)',
    borderRadius: 7,
    fontSize: 13,
    outline: 'none',
    fontFamily: "'Inter', sans-serif",
    color: '#1e293b',
    background: '#fff',
    textAlign: 'right',
    transition: 'border-color 0.15s ease',
    boxSizing: 'border-box',
  },
  amountCell: {
    padding: '8px 12px',
    textAlign: 'right',
    fontWeight: 600,
    fontSize: 13,
    color: '#059669',
    fontVariantNumeric: 'tabular-nums',
    verticalAlign: 'middle',
  },
  addLineBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 16px',
    background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
    color: '#059669',
    border: '2px dashed rgba(16,185,129,0.3)',
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 12,
    fontFamily: "'Inter', sans-serif",
    transition: 'all 0.15s ease',
  },
  removeLine: {
    background: 'none',
    border: 'none',
    color: '#ef4444',
    cursor: 'pointer',
    padding: 6,
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.15s ease',
  },
  expandLine: {
    background: 'none',
    border: 'none',
    color: '#059669',
    cursor: 'pointer',
    padding: 6,
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.15s ease',
  },
  moveBtns: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: 2,
  },
  moveBtn: {
    background: 'none',
    border: 'none',
    color: '#64748b',
    cursor: 'pointer',
    padding: '1px 4px',
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 0,
    transition: 'background 0.15s ease, color 0.15s ease',
  },
  // Line item full-view modal
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(6, 78, 59, 0.4)',
    backdropFilter: 'blur(4px)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  modalCard: {
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 24px 60px rgba(2, 44, 34, 0.35)',
    width: '100%',
    maxWidth: 680,
    maxHeight: '90vh',
    overflowY: 'auto',
    padding: 26,
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
    paddingBottom: 14,
    borderBottom: '1px solid rgba(16,185,129,0.15)',
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#064e3b',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  modalNavBtn: {
    background: '#f0fdf4',
    border: '1.5px solid rgba(16,185,129,0.25)',
    color: '#059669',
    cursor: 'pointer',
    width: 30,
    height: 30,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s ease',
  },
  modalGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 14,
    marginTop: 14,
  },
  modalLabel: {
    display: 'block',
    fontSize: 11,
    fontWeight: 700,
    color: '#047857',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 6,
  },
  modalFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 22,
    paddingTop: 16,
    borderTop: '1px solid rgba(16,185,129,0.15)',
  },
  modalDoneBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '10px 22px',
    background: 'linear-gradient(135deg, #059669, #10b981)',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: "'Inter', sans-serif",
  },
  // Bottom actions
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 12,
    padding: '20px 0 0',
    flexWrap: 'wrap',
  },
  btnPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #059669, #10b981)',
    color: '#fff',
    border: 'none',
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
    fontFamily: "'Inter', sans-serif",
  },
  btnDraft: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
    color: '#78350f',
    border: 'none',
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 4px 15px rgba(245, 158, 11, 0.25)',
    fontFamily: "'Inter', sans-serif",
  },
  btnSecondary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 24px',
    background: '#fff',
    color: '#047857',
    border: '2px solid rgba(16,185,129,0.25)',
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Inter', sans-serif",
  },
  // Loading
  loadingOverlay: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 20px',
    gap: 16,
  },
  spinner: {
    width: 44,
    height: 44,
    border: '3px solid #d1fae5',
    borderTopColor: '#059669',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  // IEC card
  iecField: {
    padding: '14px 18px',
    background: '#f0fdf4',
    borderRadius: 10,
    border: '1px solid rgba(16,185,129,0.12)',
    marginBottom: 12,
  },
  iecLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: '#047857',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  iecValue: {
    fontSize: 15,
    color: '#064e3b',
    fontWeight: 500,
  },
  // Totals row
  totalRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 32,
    padding: '16px 18px',
    background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
    borderRadius: 12,
    marginTop: 16,
    border: '1px solid rgba(16,185,129,0.15)',
  },
  totalLabel: {
    fontSize: 14,
    fontWeight: 700,
    color: '#064e3b',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  totalValue: {
    fontSize: 18,
    fontWeight: 800,
    color: '#059669',
    fontVariantNumeric: 'tabular-nums',
    fontFamily: "'Poppins', sans-serif",
  },
  totalWords: {
    fontSize: 12,
    color: '#047857',
    fontStyle: 'italic',
    marginTop: 6,
    textAlign: 'right',
  },
};

// Prevent mouse wheel from changing number inputs
const onWheelNumber = (e) => e.target.blur();

// Focus/blur handlers
const onFocusInput = (e) => {
  e.target.style.borderColor = '#10b981';
  e.target.style.boxShadow = '0 0 0 3px rgba(16,185,129,0.12)';
};
const onBlurInput = (e) => {
  e.target.style.borderColor = 'rgba(16,185,129,0.18)';
  e.target.style.boxShadow = 'none';
};

// ─── Form field context (avoids defining components inside render) ────────────
const FormFieldContext = React.createContext();

const Field = ({ label, field, type = 'text', readOnly = false, placeholder = '', colSpan, rows, required = false }) => {
  const { form, handleChange } = useContext(FormFieldContext);
  return (
    <div style={{ ...s.formGroup, gridColumn: colSpan ? `span ${colSpan}` : undefined }}>
      <label style={s.label}>
        {label}
        {required && <span style={{ color: '#ef4444', marginLeft: 4 }}>*</span>}
      </label>
      {rows ? (
        <textarea
          value={form[field] || ''}
          onChange={readOnly ? undefined : handleChange(field)}
          readOnly={readOnly}
          placeholder={placeholder}
          rows={rows}
          required={required && !readOnly}
          style={readOnly ? { ...s.inputReadOnly, minHeight: rows * 24, resize: 'vertical' } : s.textarea}
          onFocus={readOnly ? undefined : onFocusInput}
          onBlur={readOnly ? undefined : onBlurInput}
        />
      ) : (
        <input
          type={type}
          value={form[field] || ''}
          onChange={readOnly ? undefined : handleChange(field)}
          readOnly={readOnly}
          placeholder={placeholder}
          required={required && !readOnly}
          style={readOnly ? s.inputReadOnly : s.input}
          onFocus={readOnly ? undefined : onFocusInput}
          onBlur={readOnly ? undefined : onBlurInput}
          onWheel={type === 'number' ? onWheelNumber : undefined}
        />
      )}
    </div>
  );
};

const SelectField = ({ label, field, options, placeholder, required = false }) => {
  const { form, handleChange } = useContext(FormFieldContext);
  return (
    <div style={s.formGroup}>
      <label style={s.label}>
        {label}
        {required && <span style={{ color: '#ef4444', marginLeft: 4 }}>*</span>}
      </label>
      <select
        value={form[field] || ''}
        onChange={handleChange(field)}
        required={required}
        style={s.select}
        onFocus={onFocusInput}
        onBlur={onBlurInput}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );
};

// ─── Buyer Search Dropdown ──────────────────────────────────────────────────────
const BuyerSearchDropdown = ({ buyerDetails, selectedId, onSelect, onSave, showSave }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedBuyer = selectedId ? buyerDetails.find((b) => b.id === selectedId) : null;

  const filtered = search.trim()
    ? buyerDetails.filter((b) => {
        const q = search.toLowerCase();
        return (
          (b.buyer_name || '').toLowerCase().includes(q)
        );
      })
    : buyerDetails;

  const handleSelect = (buyer) => {
    onSelect(buyer);
    setSearch('');
    setOpen(false);
  };

  const handleNewBuyer = () => {
    onSelect(null);
    setSearch('');
    setOpen(false);
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <label style={s.label}>Select Buyer</label>
      <div style={{ display: 'flex', gap: 8 }}>
        <div ref={wrapperRef} style={{ flex: 1, position: 'relative' }}>
          {/* Trigger / display */}
          <div
            onClick={() => {
              setOpen(!open);
              if (!open) setTimeout(() => inputRef.current?.focus(), 50);
            }}
            style={{
              ...s.input,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              padding: '9px 14px',
              minHeight: 42,
              background: selectedBuyer ? '#f0fdf4' : '#fff',
              borderColor: selectedBuyer ? 'rgba(16,185,129,0.35)' : 'rgba(16,185,129,0.18)',
            }}
          >
            <FiSearch size={14} color="#9ca3af" style={{ flexShrink: 0 }} />
            {selectedBuyer ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', minWidth: 0 }}>
                <div style={{ minWidth: 0 }}>
                  <span style={{ fontWeight: 600, color: '#064e3b', fontSize: 13 }}>
                    {selectedBuyer.buyer_name}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNewBuyer();
                  }}
                  style={{
                    background: 'none', border: 'none', padding: '2px 4px',
                    cursor: 'pointer', color: '#9ca3af', flexShrink: 0,
                  }}
                  title="Clear selection (New Buyer)"
                >
                  <FiX size={14} />
                </button>
              </div>
            ) : (
              <span style={{ color: '#9ca3af', fontSize: 13, flex: 1 }}>
                New Buyer — type to search saved buyers...
              </span>
            )}
          </div>

          {/* Dropdown */}
          {open && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 100,
              background: '#fff',
              border: '1px solid rgba(16,185,129,0.2)',
              borderRadius: 10,
              boxShadow: '0 8px 24px rgba(6,78,59,0.12)',
              marginTop: 4,
              overflow: 'hidden',
            }}>
              {/* Search input */}
              <div style={{
                padding: '10px 12px',
                borderBottom: '1px solid rgba(16,185,129,0.1)',
                background: '#f9fafb',
              }}>
                <div style={{ position: 'relative' }}>
                  <FiSearch
                    size={14}
                    style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }}
                  />
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder="Search by buyer name or contract ref..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{
                      ...s.input,
                      width: '100%',
                      boxSizing: 'border-box',
                      paddingLeft: 32,
                      fontSize: 13,
                      margin: 0,
                    }}
                    onFocus={onFocusInput}
                    onBlur={onBlurInput}
                  />
                </div>
              </div>

              {/* "New Buyer" option */}
              <div
                onClick={handleNewBuyer}
                style={{
                  padding: '10px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  cursor: 'pointer',
                  borderBottom: '1px solid rgba(16,185,129,0.08)',
                  background: !selectedId ? '#ecfdf5' : '#fff',
                  transition: 'background 0.1s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#ecfdf5'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = !selectedId ? '#ecfdf5' : '#fff'; }}
              >
                <div style={{
                  width: 30, height: 30, borderRadius: 8,
                  background: 'linear-gradient(135deg, #059669, #10b981)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <FiUserPlus size={14} color="#fff" />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#064e3b' }}>New Buyer</div>
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>Enter buyer details manually</div>
                </div>
              </div>

              {/* Buyer list */}
              <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                {filtered.length === 0 ? (
                  <div style={{ padding: '16px 14px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                    {search ? 'No buyers match your search' : 'No saved buyers yet'}
                  </div>
                ) : (
                  filtered.map((buyer) => (
                    <div
                      key={buyer.id}
                      onClick={() => handleSelect(buyer)}
                      style={{
                        padding: '10px 14px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        borderBottom: '1px solid rgba(16,185,129,0.06)',
                        background: selectedId === buyer.id ? '#ecfdf5' : '#fff',
                        transition: 'background 0.1s ease',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#f0fdf4'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = selectedId === buyer.id ? '#ecfdf5' : '#fff'; }}
                    >
                      <div style={{
                        width: 30, height: 30, borderRadius: 8,
                        background: '#ecfdf5', border: '1px solid rgba(16,185,129,0.2)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 700, color: '#059669', flexShrink: 0,
                      }}>
                        {(buyer.buyer_name || '?')[0].toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 13, fontWeight: 600, color: '#1e293b',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {buyer.buyer_name}
                        </div>
                      </div>
                      {selectedId === buyer.id && (
                        <FiCheckCircle size={14} color="#059669" style={{ flexShrink: 0 }} />
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Save as Buyer button */}
        {showSave && (
          <button
            type="button"
            onClick={onSave}
            style={{
              padding: '8px 16px',
              background: '#ecfdf5',
              border: '1px solid rgba(16,185,129,0.3)',
              borderRadius: 8,
              color: '#059669',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <FiSave size={12} /> Save Buyer
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Component ──────────────────────────────────────────────────────────────────
export default function CommercialForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { activeCompany } = useAuth();
  const isEdit = Boolean(id);

  // State
  const [form, setForm] = useState(() => ({ ...initialFormState }));
  const [lineItems, setLineItems] = useState([{ ...EMPTY_LINE_ITEM, _key: nextLineKey() }]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [activeTab, setActiveTab] = useState('invoice');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [companyData, setCompanyData] = useState(null);


  // Package types from settings
  const [packageTypes, setPackageTypes] = useState([]);
  // "Other..." on the packages-total unit: typed here and saved back to settings
  const [addingPkgUnit, setAddingPkgUnit] = useState(false);
  const [newPkgUnit, setNewPkgUnit] = useState('');

  // The typed unit is taken on the tick or on Enter, never on losing focus:
  // clicking the tick blurs the box, and a commit on blur would race it.
  const commitPkgUnit = async () => {
    const name = newPkgUnit.trim().toUpperCase();
    if (!name) { setAddingPkgUnit(false); setNewPkgUnit(''); return; }
    setForm((prev) => ({ ...prev, total_package_unit: name }));
    setAddingPkgUnit(false);
    setNewPkgUnit('');
    // A unit the company already keeps is simply chosen, not added twice.
    if (packageTypes.some((pt) => pt.name.toUpperCase() === name)) return;
    try {
      const res = await settingsAPI.createPackageType({ name });
      setPackageTypes((prev) => [...prev, res.data]);
      toast.success('Package unit added');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add package unit');
    }
  };

  // Buyer details state
  const [buyerDetails, setBuyerDetails] = useState([]);
  const [loadingBuyers, setLoadingBuyers] = useState(false);

  // Proforma link state
  const [proformaList, setProformaList] = useState([]);
  const [selectedProformaIds, setSelectedProformaIds] = useState([]);
  const [proformaSearch, setProformaSearch] = useState('');
  const [proformaDropdownOpen, setProformaDropdownOpen] = useState(false);
  const proformaDropdownRef = React.useRef(null);
  const proformaIdFromUrl = searchParams.get('proformaId');


  // ── Auto-populate from company ──────────────────────────────────────────────
  useEffect(() => {
    if (activeCompany) {
      const compInfo = {
        exporter_name: activeCompany.exporter_name || '',
        exporter_address: activeCompany.exporter_address || activeCompany.address || '',
        iec: activeCompany.iec || activeCompany.iec_number || '',
      };
      setCompanyData(activeCompany);
      if (!isEdit) {
        setForm((prev) => ({
          ...prev,
          ...compInfo,
        }));
      }
    }
  }, [activeCompany, isEdit]);

  // ── Fetch company details ───────────────────────────────────────────────────
  useEffect(() => {
    const fetchCompanyDetails = async () => {
      if (activeCompany?.id) {
        try {
          const res = await companyAPI.getById(activeCompany.id);
          setCompanyData(res.data);
          // companyData is used for IEC display tab

        } catch {
          // use what we have from context
        }
      }
    };
    fetchCompanyDetails();
  }, [activeCompany]);

  // ── Fetch buyer details for dropdown ────────────────────────────────────────
  useEffect(() => {
    if (!activeCompany?.id) return;
    const fetchBuyers = async () => {
      setLoadingBuyers(true);
      try {
        const res = await buyerDetailAPI.getAll();
        setBuyerDetails(Array.isArray(res.data) ? res.data : []);
      } catch { /* ignore */ }
      finally { setLoadingBuyers(false); }
    };
    fetchBuyers();
  }, [activeCompany]);

  // ── Fetch package types from settings ────────────────────────────────────
  useEffect(() => {
    if (!activeCompany?.id) return;
    settingsAPI.getPackageTypes().then(res => {
      setPackageTypes(Array.isArray(res.data) ? res.data : []);
    }).catch(() => {});
  }, [activeCompany]);

  // ── Fetch proforma list for linking ───────────────────────────────────────
  useEffect(() => {
    if (!activeCompany?.id) return;
    const fetchProformas = async () => {
      try {
        const res = await proformaAPI.getAll({});
        setProformaList(res.data || []);
      } catch { /* ignore */ }
    };
    fetchProformas();
  }, [activeCompany]);

  // Close proforma dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (proformaDropdownRef.current && !proformaDropdownRef.current.contains(e.target)) {
        setProformaDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // ── Auto-populate from proforma (when creating commercial invoice from proforma) ──
  useEffect(() => {
    if (!proformaIdFromUrl || isEdit) return;
    const populateFromProforma = async () => {
      try {
        const res = await proformaAPI.getById(proformaIdFromUrl);
        const pi = res.data;
        setForm((prev) => ({
          ...prev,
          document_type: 'COMMERCIAL INVOICE',
          proforma_id: pi.id,
          buyer_name: pi.buyer_name || '',
          buyer_address: pi.buyer_address || '',
          buyer_details: pi.buyer_details || '',
          buyer_email: pi.buyer_email || '',
          buyer_phone: pi.buyer_phone || '',
          buyer_detail_id: pi.buyer_detail_id || null,
          mode_of_shipment: normalizeShipmentMode(pi.mode_of_shipment) || prev.mode_of_shipment,
          shipment_from: pi.shipment_from || '',
          shipment_to: pi.shipment_to || '',
          terms_of_delivery: pi.terms_of_delivery || '',
          mode_of_payment: pi.mode_of_payment || '',
          lc_no: pi.lc_no || '',
          lc_amendment_ref: pi.lc_amendment_ref || '',
          lc_amendment_ref_date: pi.lc_amendment_ref_date || '',
          lc_opening_date: pi.lc_opening_date || '',
          lc_opening_bank: pi.lc_opening_bank || '',
          lc_value_usd: pi.lc_value_usd || '',
          commodity: pi.commodity || '',
          country_of_origin: pi.country_of_origin || 'INDIA',
          advising_bank: pi.advising_bank || prev.advising_bank,
          terms_and_conditions: pi.terms_and_conditions || prev.terms_and_conditions,
        }));
        // Also populate line items from proforma
        if (pi.lineItems && pi.lineItems.length > 0) {
          setLineItems(pi.lineItems.map((li) => ({
            _key: nextLineKey(),
            marks_and_nos: li.marks_and_nos || '',
            hs_code: li.hs_code || '',
            description_of_goods: li.description_of_goods || '',
            quantity: li.quantity ?? '',
            uom: li.uom || '',
            rate_usd: li.rate_usd ?? '',
            amount_usd: li.amount_usd ?? 0,
            no_of_packages: li.no_of_packages ?? '',
            package_type: li.package_type || '',
            net_weight_kg: li.net_weight_kg ?? '',
            gross_weight_kg: li.gross_weight_kg ?? '',
          })));
        }
        setSelectedProformaIds([pi.id]);
        toast.success('Pre-filled from Proforma Invoice #' + pi.invoice_no);
      } catch {
        toast.error('Failed to load proforma invoice');
      }
    };
    populateFromProforma();
  }, [proformaIdFromUrl, isEdit]);

  // ── Fetch existing document ─────────────────────────────────────────────────
  useEffect(() => {
    if (!isEdit) {
      // Get next invoice number for new docs
      const getNextNumber = async () => {
        try {
          const res = await documentAPI.getNextNumber();
          setForm((prev) => ({ ...prev, invoice_no: res.data.invoice_no || res.data.next_number || '' }));
        } catch {
          // ignore
        }
      };
      getNextNumber();
      return;
    }
    if (!isEdit) return;

    const fetchDocument = async () => {
      setLoading(true);
      try {
        const res = await documentAPI.getById(id);
        const doc = res.data;

        // Map doc fields to form
        const mapped = {};
        Object.keys(initialFormState).forEach((key) => {
          if (doc[key] !== undefined && doc[key] !== null) {
            // Handle date fields
            if (['document_date', 'lc_opening_date', 'lc_amendment_ref_date', 'despatch_date', 'sb_date', 'export_date', 'due_date', 'payment_date'].includes(key)) {
              mapped[key] = fmtDate(doc[key]);
            } else {
              mapped[key] = doc[key];
            }
          }
        });
        if (mapped.mode_of_shipment) mapped.mode_of_shipment = normalizeShipmentMode(mapped.mode_of_shipment);

        // TR/BOE fields come back nested under trBoe
        if (doc.trBoe) {
          mapped.despatch_doc_no = doc.trBoe.despatch_doc_no || '';
          mapped.despatch_date = fmtDate(doc.trBoe.despatch_date);
          mapped.carrier = doc.trBoe.carrier || mapped.carrier || '';
          mapped.vehicle_no = doc.trBoe.vehicle_no || '';
          mapped.sb_no = doc.trBoe.sb_no || '';
          mapped.sb_date = fmtDate(doc.trBoe.sb_date);
          mapped.export_date = fmtDate(doc.trBoe.export_date);
          mapped.to_the_order_of = doc.trBoe.to_the_order_of || '';
          mapped.notify = doc.trBoe.notify || '';
          mapped.drafts_drawn = doc.trBoe.drafts_drawn || '';
        }

        // Company auto-fill
        if (activeCompany) {
          mapped.exporter_name = mapped.exporter_name || activeCompany.exporter_name || '';
          mapped.exporter_address = mapped.exporter_address || activeCompany.exporter_address || activeCompany.address || '';
          mapped.iec = mapped.iec || activeCompany.iec || activeCompany.iec_number || '';
        }

        setForm((prev) => ({ ...prev, ...mapped }));

        // Restore multi-select proforma IDs
        if (doc.linked_proforma_ids) {
          try {
            const ids = doc.linked_proforma_ids.split(',').map(Number).filter(Boolean);
            setSelectedProformaIds(ids);
          } catch { /* ignore */ }
        } else if (doc.proforma_id) {
          setSelectedProformaIds([doc.proforma_id]);
        }

        // Line items (backend returns lineItems camelCase)
        const items = doc.lineItems || doc.line_items;
        if (items && Array.isArray(items) && items.length > 0) {
          setLineItems(items.map((li) => ({
            _key: nextLineKey(),
            marks_and_nos: li.marks_and_nos || '',
            hs_code: li.hs_code || '',
            description_of_goods: li.description_of_goods || '',
            quantity: li.quantity ?? '',
            uom: li.uom || '',
            rate_usd: li.rate_usd ?? '',
            amount_usd: li.amount_usd ?? 0,
            no_of_packages: li.no_of_packages ?? '',
            package_type: li.package_type || '',
            net_weight_kg: li.net_weight_kg ?? '',
            gross_weight_kg: li.gross_weight_kg ?? '',
            id: li.id,
          })));
        }
      } catch (err) {
        toast.error(err.response?.data?.error || 'Failed to load document');
        navigate('/commercial-invoices');
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();
  }, [id, isEdit, navigate, activeCompany]);

  // ── Line item calculations ──────────────────────────────────────────────────
  useEffect(() => {
    const updatedItems = lineItems.map((item) => {
      const qty = parseFloat(item.quantity) || 0;
      const rate = parseFloat(item.rate_usd) || 0;
      return { ...item, amount_usd: qty * rate };
    });

    // Only update if amounts actually changed
    const changed = updatedItems.some((item, i) => item.amount_usd !== lineItems[i].amount_usd);
    if (changed) {
      setLineItems(updatedItems);
    }

    const totalUsd = updatedItems.reduce((sum, item) => sum + (item.amount_usd || 0), 0);
    const totalPkgs = updatedItems.reduce((sum, item) => sum + (parseInt(item.no_of_packages) || 0), 0);
    const totalNetWt = updatedItems.reduce((sum, item) => sum + (parseFloat(item.net_weight_kg) || 0), 0);
    const totalGrossWt = updatedItems.reduce((sum, item) => sum + (parseFloat(item.gross_weight_kg) || 0), 0);
    setForm((prev) => {
      const newTotalPkgs = totalPkgs ? String(totalPkgs) : '';
      const newTotalNetWt = totalNetWt ? totalNetWt.toFixed(2) : '';
      const newTotalGrossWt = totalGrossWt ? totalGrossWt.toFixed(2) : '';
      if (prev.total_usd !== totalUsd || prev.total_packages !== newTotalPkgs || prev.total_net_weight !== newTotalNetWt || prev.total_gross_weight !== newTotalGrossWt) {
        return {
          ...prev,
          total_usd: totalUsd,
          total_in_words: toWords(totalUsd),
          total_packages: newTotalPkgs,
          total_net_weight: newTotalNetWt,
          total_gross_weight: newTotalGrossWt,
        };
      }
      return prev;
    });
  }, [lineItems]);

  // Auto-fill carrier from mode_of_shipment
  useEffect(() => {
    const carrierMap = {
      Sea: 'By Sea',
      Air: 'By Air',
      Road: 'By Road',
      'By Road / By Truck': 'By Road',
      Rail: 'By Rail',
      Multimodal: 'Multimodal',
    };
    setForm((prev) => {
      const auto = carrierMap[prev.mode_of_shipment];
      if (!auto || prev.carrier === auto) return prev;
      // Keep a manually typed carrier; only replace empty or auto-filled values
      if (prev.carrier && !Object.values(carrierMap).includes(prev.carrier)) return prev;
      return { ...prev, carrier: auto };
    });
  }, [form.mode_of_shipment]);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleLineChange = (index, field) => (e) => {
    const newItems = [...lineItems];
    newItems[index] = { ...newItems[index], [field]: e.target.value };
    setLineItems(newItems);
  };

  const addLineItem = () => {
    setLineItems((prev) => [...prev, { ...EMPTY_LINE_ITEM, _key: nextLineKey() }]);
  };

  const removeLineItem = (index) => {
    if (lineItems.length <= 1) {
      toast.warning('At least one line item is required');
      return;
    }
    setLineItems((prev) => prev.filter((_, i) => i !== index));
  };

  const moveLineItem = (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= lineItems.length) return;
    setLineItems((prev) => {
      const items = [...prev];
      [items[index], items[target]] = [items[target], items[index]];
      return items;
    });
  };


  // ── Attachments ───────────────────────────────────────────────────────
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Fetch attachments when editing
  useEffect(() => {
    if (isEdit && id) {
      const fetchAttachments = async () => {
        try {
          const res = await attachmentAPI.list(id);
          setAttachments(res.data || []);
        } catch {
          // ignore
        }
      };
      fetchAttachments();
    }
  }, [isEdit, id]);

  const handleFileUpload = async (files) => {
    if (!id) {
      toast.warning('Please save the document first before uploading files');
      return;
    }
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const res = await attachmentAPI.upload(id, Array.from(files));
      setAttachments((prev) => [...(res.data || []), ...prev]);
      toast.success(`${files.length} file(s) uploaded successfully`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadAttachment = async (att) => {
    try {
      const res = await attachmentAPI.download(att.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', att.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download file');
    }
  };

  const handleDeleteAttachment = async (att) => {
    if (!window.confirm(`Delete "${att.file_name}"?`)) return;
    try {
      await attachmentAPI.remove(att.id);
      setAttachments((prev) => prev.filter((a) => a.id !== att.id));
      toast.success('File deleted');
    } catch {
      toast.error('Failed to delete file');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileUpload(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  // ── Save ────────────────────────────────────────────────────────────────────
  const handleSave = async (asDraft = false) => {
    if (!asDraft) {
      const missing = [];
      if (!form.invoice_no) missing.push('Invoice No');
      if (!form.buyer_name) missing.push('Buyer Name');
      if (!form.buyer_address) missing.push('Buyer Address');
      if (!form.mode_of_shipment) missing.push('Mode of Shipment');
      if (!form.shipment_from) missing.push('Shipment From');
      if (!form.shipment_to) missing.push('Shipment To');
      if (!form.hs_code) missing.push('HS Code');
      if (missing.length > 0) {
        toast.error('Required fields missing: ' + missing.join(', '));
        return;
      }
      if (form.buyer_name && !form.buyer_detail_id) {
        toast.error('Please select a saved buyer or save buyer details first');
        return;
      }
      const hasValidLineItem = lineItems.some((li) => li.description_of_goods && (parseFloat(li.quantity) > 0));
      if (!hasValidLineItem) {
        toast.error('At least one line item with description and quantity is required');
        return;
      }
    } else {
      if (!form.invoice_no) {
        toast.error('Invoice number is required');
        return;
      }
    }

    setSaving(true);
    try {
      // Helper: convert empty strings to null for optional numeric fields
      const toFloat = (v) => (v !== '' && v != null && !isNaN(v)) ? parseFloat(v) : null;
      const toInt = (v) => (v !== '' && v != null && !isNaN(v)) ? parseInt(v, 10) : null;
      const toStr = (v) => (v !== '' && v != null) ? String(v) : null;

      const lineItemsPayload = lineItems.map((li) => ({
        marks_and_nos: li.marks_and_nos || '',
        hs_code: li.hs_code || '',
        description_of_goods: li.description_of_goods || '',
        quantity: parseFloat(li.quantity) || 0,
        uom: li.uom || '',
        rate_usd: parseFloat(li.rate_usd) || 0,
        no_of_packages: toInt(li.no_of_packages),
        package_type: li.package_type || '',
        net_weight_kg: toFloat(li.net_weight_kg),
        gross_weight_kg: toFloat(li.gross_weight_kg),
      }));

      const payload = {
        document_date: form.document_date || '',
        buyer_name: form.buyer_name || '',
        buyer_address: form.buyer_address || '',
        buyer_details: form.buyer_details || '',
        buyer_email: form.buyer_email || '',
        buyer_phone: form.buyer_phone || '',
        mode_of_shipment: form.mode_of_shipment || '',
        shipment_from: form.shipment_from || '',
        shipment_to: form.shipment_to || '',
        terms_of_delivery: form.terms_of_delivery || '',
        mode_of_payment: form.mode_of_payment || '',
        lc_no: form.lc_no || '',
        lc_amendment_ref: form.lc_amendment_ref || '',
        lc_amendment_ref_date: toStr(form.lc_amendment_ref_date),
        lc_opening_date: toStr(form.lc_opening_date),
        lc_opening_bank: form.lc_opening_bank || '',
        lc_opening_bank_address: form.lc_opening_bank_address || '',
        lc_value_usd: toFloat(form.lc_value_usd),
        insurance_ref: form.insurance_ref || '',
        insurance_company: form.insurance_company || '',
        hs_code: form.hs_code || '',
        total_packages: toInt(form.total_packages),
        total_net_weight: toFloat(form.total_net_weight),
        total_gross_weight: toFloat(form.total_gross_weight),
        total_package_unit: form.total_package_unit || '',
        other_ref: form.other_ref || '',
        other_ref_2: form.other_ref_2 || '',
        declaration: form.declaration || '',
        fob_value_usd: toFloat(form.fob_value_usd),
        freight_charges_usd: toFloat(form.freight_charges_usd),
        bill_ref: form.bill_ref || '',
        due_date: toStr(form.due_date),
        payment_date: toStr(form.payment_date),
        fob_inr: toFloat(form.fob_inr),
        drawback: toFloat(form.drawback),
        commodity: form.commodity || '',
        country_of_origin: form.country_of_origin || 'INDIA',
        advising_bank: form.advising_bank || '',
        terms_and_conditions: form.terms_and_conditions || '',
        status: asDraft ? 'draft' : 'saved',
        proforma_id: form.proforma_id || null,
        buyer_detail_id: form.buyer_detail_id || null,
        lineItems: lineItemsPayload,
        trBoe: {
          despatch_doc_no: form.despatch_doc_no || '',
          despatch_date: toStr(form.despatch_date),
          carrier: form.carrier || '',
          vehicle_no: form.vehicle_no || '',
          sb_no: form.sb_no || '',
          sb_date: toStr(form.sb_date),
          export_date: toStr(form.export_date),
          to_the_order_of: form.to_the_order_of || '',
          notify: form.notify || '',
          drafts_drawn: form.drafts_drawn || '',
        },
      };

      if (isEdit) {
        await documentAPI.update(id, payload);
        toast.success('Document updated successfully');
      } else {
        const res = await documentAPI.create(payload);
        toast.success('Document created successfully');

        // Save buyer as contact
        if (form.buyer_name && form.buyer_name.trim() !== '') {
          try {
            await contactAPI.saveBuyer({
              name: form.buyer_name,
              address: form.buyer_address || '',
              country: 'INDIA',
              email: form.buyer_email || '',
              phone: form.buyer_phone || '',
            });
          } catch (err) {
            // silently ignore duplicate buyer contacts
          }
        }

        if (res.data?.id) {
          navigate(`/commercial-invoices/edit/${res.data.id}`, { replace: true });
        }
      }

      setForm((prev) => ({ ...prev, status: asDraft ? 'draft' : 'saved' }));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save document');
    } finally {
      setSaving(false);
    }
  };

  // ── Loading state ───────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={s.loadingOverlay}>
        <div style={s.spinner} />
        <span style={{ color: '#6b7280', fontSize: 14 }}>Loading document...</span>
      </div>
    );
  }

  // ── RENDER ──────────────────────────────────────────────────────────────────
  return (
    <FormFieldContext.Provider value={{ form, handleChange }}>
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      style={s.page}
    >
      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div style={s.header}>
        <div style={s.titleRow}>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/commercial-invoices')}
            style={s.backBtn}
            title="Back"
          >
            <FiArrowLeft size={18} />
          </motion.button>
          <div style={s.titleIcon}>
            <FiFileText size={24} color="#fff" />
          </div>
          <div>
            <h1 style={s.title}>
              {isEdit ? 'Edit Commercial Invoice' : 'New Commercial Invoice'}
              {form.invoice_no && (
                <span style={{ fontWeight: 400, fontSize: 16, color: '#059669', marginLeft: 10 }}>
                  #{form.invoice_no}
                </span>
              )}
            </h1>
            <p style={s.subtitle}>
              {activeCompany?.exporter_name || 'Shipment Document'}
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={s.statusBadge(form.status)}>
            {form.status === 'finalized' ? <FiCheckCircle size={13} /> : <FiAlertCircle size={13} />}
            {s.statusLabel(form.status)}
          </div>
        </div>
      </div>

      {/* ── Tabs ─────────────────────────────────────────────────────────── */}
      <div style={s.tabBar}>
        {TABS.map((tab) => (
          <motion.button
            key={tab.key}
            whileHover={{ scale: activeTab === tab.key ? 1 : 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setActiveTab(tab.key)}
            style={s.tab(activeTab === tab.key)}
          >
            <tab.icon size={16} />
            {tab.label}
          </motion.button>
        ))}
        {isEdit && (
          <motion.button
            whileHover={{ scale: activeTab === 'attachments' ? 1 : 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setActiveTab('attachments')}
            style={s.tab(activeTab === 'attachments')}
          >
            <FiPaperclip size={16} />
            Attachments
            {attachments.length > 0 && (
              <span style={{
                background: activeTab === 'attachments' ? 'rgba(255,255,255,0.3)' : 'rgba(16,185,129,0.15)',
                color: activeTab === 'attachments' ? '#fff' : '#059669',
                padding: '2px 8px',
                borderRadius: 10,
                fontSize: 11,
                fontWeight: 700,
              }}>
                {attachments.length}
              </span>
            )}
          </motion.button>
        )}
      </div>

      {/* ── Tab Content ──────────────────────────────────────────────────── */}
      <AnimatePresence mode="wait">
        {/* ════════════════════════════════════════════════════════════════ */}
        {/* TAB 1: COMMERCIAL INVOICE                                      */}
        {/* ════════════════════════════════════════════════════════════════ */}
        {activeTab === 'invoice' && (
          <motion.div
            key="invoice"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.25 }}
          >
            {/* ── Exporter (auto-populated, read-only) ──────────────────── */}
            <div style={s.card}>
              <div style={s.cardTitle}>
                <div style={s.cardTitleIcon}><FiGlobe size={16} /></div>
                Exporter Information
                <span style={{ fontSize: 11, fontWeight: 500, color: '#94a3b8', marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <FiLock size={11} /> Auto-populated from company
                </span>
              </div>
              <div style={s.grid4}>
                <div style={s.formGroup}>
                  <label style={s.label}>Invoice No</label>
                  <input
                    type="text"
                    value={form.invoice_no || ''}
                    onChange={handleChange('invoice_no')}
                    placeholder="INV-XXXX"
                    style={s.input}
                    onFocus={onFocusInput}
                    onBlur={onBlurInput}
                  />
                </div>
                <Field label="Date" field="document_date" type="date" />
                <Field label="Exporter Name" field="exporter_name" readOnly />
                <Field label="IEC" field="iec" readOnly />
              </div>
              <div style={{ marginTop: 16 }}>
                <Field label="Exporter Address" field="exporter_address" readOnly rows={2} />
              </div>
            </div>

            {/* ── Proforma Link ────────────────────────────────────────── */}
              <div style={s.card}>
                <div style={s.cardTitle}>
                  <div style={s.cardTitleIcon}><FiFileText size={16} /></div>
                  Link to Proforma Invoice
                </div>
                <div style={s.grid2}>
                  <div>
                    <label style={s.label}>Proforma Invoice</label>
                    <select
                      value={form.proforma_id || ''}
                      onChange={async (e) => {
                        const pid = e.target.value ? parseInt(e.target.value) : null;
                        if (!pid) {
                          setForm((prev) => ({
                            ...prev,
                            proforma_id: null,
                            buyer_name: '', buyer_address: '', buyer_details: '',
                            buyer_email: '', buyer_phone: '', buyer_detail_id: null,
                          }));
                          setLineItems([{ ...EMPTY_LINE_ITEM, _key: nextLineKey() }]);
                          return;
                        }
                        try {
                          const res = await proformaAPI.getById(pid);
                          const pi = res.data;
                          setForm((prev) => ({
                            ...prev,
                            proforma_id: pid,
                            buyer_name: pi.buyer_name || '',
                            buyer_address: pi.buyer_address || '',
                            buyer_details: pi.buyer_details || '',
                            buyer_email: pi.buyer_email || '',
                            buyer_phone: pi.buyer_phone || '',
                            buyer_detail_id: pi.buyer_detail_id || null,
                            mode_of_shipment: normalizeShipmentMode(pi.mode_of_shipment) || prev.mode_of_shipment,
                            shipment_from: pi.shipment_from || prev.shipment_from,
                            shipment_to: pi.shipment_to || prev.shipment_to,
                            terms_of_delivery: pi.terms_of_delivery || prev.terms_of_delivery,
                            mode_of_payment: pi.mode_of_payment || prev.mode_of_payment,
                            commodity: pi.commodity || prev.commodity,
                            lc_no: pi.lc_no || prev.lc_no,
                            lc_amendment_ref: pi.lc_amendment_ref || prev.lc_amendment_ref,
                            lc_amendment_ref_date: pi.lc_amendment_ref_date || prev.lc_amendment_ref_date,
                            lc_opening_date: pi.lc_opening_date || prev.lc_opening_date,
                            lc_opening_bank: pi.lc_opening_bank || prev.lc_opening_bank,
                            lc_value_usd: pi.lc_value_usd || prev.lc_value_usd,
                            country_of_origin: pi.country_of_origin || prev.country_of_origin,
                            advising_bank: pi.advising_bank || prev.advising_bank,
                            terms_and_conditions: pi.terms_and_conditions || prev.terms_and_conditions,
                          }));
                          if (pi.lineItems && pi.lineItems.length > 0) {
                            setLineItems(pi.lineItems.map((li) => ({
                              _key: nextLineKey(),
                              marks_and_nos: li.marks_and_nos || '',
                              hs_code: li.hs_code || '',
                              description_of_goods: li.description_of_goods || '',
                              quantity: li.quantity ?? '',
                              uom: li.uom || '',
                              rate_usd: li.rate_usd ?? '',
                              amount_usd: li.amount_usd ?? 0,
                              no_of_packages: li.no_of_packages ?? '',
                              package_type: li.package_type || '',
                              net_weight_kg: li.net_weight_kg ?? '',
                              gross_weight_kg: li.gross_weight_kg ?? '',
                            })));
                          }
                          toast.success('Pre-filled from Proforma Invoice #' + pi.invoice_no);
                        } catch {
                          toast.error('Failed to load proforma details');
                        }
                      }}
                      style={s.input}
                    >
                      <option value="">-- None --</option>
                      {proformaList.map((pi) => (
                        <option key={pi.id} value={pi.id}>
                          {pi.invoice_no} — {pi.buyer_name} ({pi.document_date})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

            {/* ── Buyer ─────────────────────────────────────────────────── */}
            <div style={s.card}>
              <div style={s.cardTitle}>
                <div style={s.cardTitleIcon}><FiCreditCard size={16} /></div>
                Buyer / Consignee Details
              </div>
              {/* Buyer Selector - hidden when proforma is linked */}
              {!form.proforma_id && (
                <BuyerSearchDropdown
                  buyerDetails={buyerDetails}
                  selectedId={form.buyer_detail_id}
                  onSelect={(buyer) => {
                    if (buyer) {
                      setForm((prev) => {
                        const lcBank = [prev.lc_opening_bank, prev.lc_opening_bank_address]
                          .filter(Boolean).join(', ');
                        return {
                          ...prev,
                          buyer_detail_id: buyer.id,
                          buyer_name: buyer.buyer_name || '',
                          buyer_address: buyer.buyer_address || '',
                          buyer_details: buyer.details || '',
                          buyer_email: buyer.email || '',
                          buyer_phone: buyer.phone_no || '',
                          notify: [buyer.buyer_name, buyer.buyer_address, lcBank ? `AND ${lcBank}` : '']
                            .filter(Boolean).join('\n'),
                        };
                      });
                    } else {
                      setForm((prev) => ({
                        ...prev,
                        buyer_detail_id: null,
                        buyer_name: '',
                        buyer_address: '',
                        buyer_details: '',
                        buyer_email: '',
                        buyer_phone: '',
                        notify: '',
                      }));
                    }
                  }}
                  onSave={async () => {
                    if (!form.buyer_name.trim()) return;
                    try {
                      const res = await buyerDetailAPI.create({
                        buyer_name: form.buyer_name,
                        buyer_address: form.buyer_address,
                        details: form.buyer_details || '',
                        email: form.buyer_email || '',
                        phone_no: form.buyer_phone || '',
                      });
                      setForm((prev) => ({ ...prev, buyer_detail_id: res.data.id }));
                      setBuyerDetails((prev) => [...prev, res.data]);
                      toast.success('Buyer saved');
                    } catch (err) {
                      toast.error(err.response?.data?.error || 'Failed to save buyer');
                    }
                  }}
                  showSave={!!form.buyer_name && !form.buyer_detail_id}
                />
              )}
              {form.proforma_id ? (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '6px 12px', marginBottom: 12,
                  background: '#ecfdf5', borderRadius: 8,
                  border: '1px solid rgba(16,185,129,0.2)',
                  fontSize: 12, color: '#047857', fontWeight: 500,
                }}>
                  <FiLock size={11} />
                  Buyer details auto-filled from linked Proforma Invoice.
                </div>
              ) : form.buyer_detail_id ? (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '6px 12px', marginBottom: 12,
                  background: '#ecfdf5', borderRadius: 8,
                  border: '1px solid rgba(16,185,129,0.2)',
                  fontSize: 12, color: '#047857', fontWeight: 500,
                }}>
                  <FiLock size={11} />
                  Buyer details auto-filled from saved buyer. Click the X above to enter manually.
                </div>
              ) : null}
              <div style={s.grid2}>
                <Field label="Buyer Name" field="buyer_name" placeholder="Enter buyer name" required readOnly={!!form.buyer_detail_id || !!form.proforma_id} />
              </div>
              <div style={{ marginTop: 16 }}>
                <Field label="Buyer Address" field="buyer_address" placeholder="Full buyer address" rows={2} required readOnly={!!form.buyer_detail_id || !!form.proforma_id} />
              </div>
              <div style={{ marginTop: 16 }}>
                <Field label="Buyer Details" field="buyer_details" placeholder="e.g. IRC NO. 123456, VAT/BIN REG. NO. 78910 (printed below address)" rows={2} />
              </div>
              <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <Field label="Buyer Email" field="buyer_email" type="email" placeholder="buyer@example.com" readOnly={!!form.buyer_detail_id || !!form.proforma_id} />
                <Field label="Buyer Phone" field="buyer_phone" placeholder="+91-9876543210" readOnly={!!form.buyer_detail_id || !!form.proforma_id} />
              </div>
            </div>

            {/* ── Shipment Details ──────────────────────────────────────── */}
            <div style={s.card}>
              <div style={s.cardTitle}>
                <div style={s.cardTitleIcon}><FiTruck size={16} /></div>
                Shipment Details
              </div>
              <div style={s.grid3}>
                <SelectField label="Mode of Shipment" field="mode_of_shipment" options={SHIPMENT_MODES} required />
                <Field label="Shipment From" field="shipment_from" placeholder="Port of loading" required />
                <Field label="Shipment To" field="shipment_to" placeholder="Port of discharge" required />
              </div>
              <div style={{ ...s.grid3, marginTop: 16 }}>
                <Field label="Terms of Delivery" field="terms_of_delivery" placeholder="e.g. FOB, CIF, CFR" rows={2} />
                <Field label="Mode of Payment" field="mode_of_payment" placeholder="e.g. AT SIGHT, L/C, T/T" />
              </div>
            </div>

            {/* ── L/C Details ────────────────────────────────────────── */}
            <div style={s.card}>
              <div style={s.cardTitle}>
                <div style={s.cardTitleIcon}><FiDollarSign size={16} /></div>
                Letter of Credit Details
              </div>
              <div style={s.grid2}>
                <Field label="L/C No" field="lc_no" placeholder="L/C number" required />
                <Field label="L/C Opening Date" field="lc_opening_date" type="date" required />
              </div>
              <div style={{ ...s.grid2, marginTop: 16 }}>
                <Field label="L/C Opening Bank" field="lc_opening_bank" placeholder="Bank name" required />
                <Field label="L/C Value (USD)" field="lc_value_usd" type="number" placeholder="0.00" />
              </div>
              <div style={{ ...s.grid2, marginTop: 16 }}>
                <Field label="L/C Amendment Ref" field="lc_amendment_ref" placeholder="Amendment ref" />
                <Field label="L/C Amendment Ref Date" field="lc_amendment_ref_date" type="date" />
              </div>
              <div style={{ marginTop: 16 }}>
                <Field label="L/C Opening Bank Address" field="lc_opening_bank_address" placeholder="Branch, address (printed after bank name in L/C No. on PDF)" />
              </div>
            </div>

            {/* ── Insurance ──────────────────────────────────────────── */}
            <div style={s.card}>
              <div style={s.cardTitle}>
                <div style={s.cardTitleIcon}><FiInfo size={16} /></div>
                Insurance
              </div>
              <div style={s.grid2}>
                <Field label="Insurance Ref" field="insurance_ref" placeholder="Policy/Ref number" />
                <Field label="Insurance Company" field="insurance_company" placeholder="Insurance company name" />
              </div>
            </div>

            {/* ── Customs / HS Code Details ──────────────────────────── */}
            <div style={s.card}>
              <div style={s.cardTitle}>
                <div style={s.cardTitleIcon}><FiHash size={16} /></div>
                Customs / HS Code Details
              </div>
              <div style={s.grid4}>
                <Field label="HS Code" field="hs_code" placeholder="e.g. 6109.1000" required />
                <Field label="Total Packages" field="total_packages" placeholder="Auto from line items" readOnly />
                <div style={s.formGroup}>
                  <label style={s.label}>Package Unit</label>
                  {addingPkgUnit ? (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input
                        type="text"
                        value={newPkgUnit}
                        onChange={(e) => setNewPkgUnit(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); commitPkgUnit(); }
                          if (e.key === 'Escape') { setAddingPkgUnit(false); setNewPkgUnit(''); }
                        }}
                        placeholder="Type a new unit..."
                        style={{ ...s.input, flex: 1, minWidth: 0 }}
                        autoFocus
                      />
                      <button
                        type="button"
                        title="Add this unit"
                        onClick={commitPkgUnit}
                        style={s.pkgUnitBtn}
                      >
                        <FiCheck size={16} />
                      </button>
                      <button
                        type="button"
                        title="Back to the list"
                        onClick={() => { setAddingPkgUnit(false); setNewPkgUnit(''); }}
                        style={{ ...s.pkgUnitBtn, color: '#9ca3af' }}
                      >
                        <FiX size={16} />
                      </button>
                    </div>
                  ) : (
                    <select
                      value={form.total_package_unit || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '__other__') { setNewPkgUnit(''); setAddingPkgUnit(true); return; }
                        setForm((prev) => ({ ...prev, total_package_unit: val }));
                      }}
                      style={s.select}
                      onFocus={onFocusInput}
                      onBlur={onBlurInput}
                    >
                      <option value="">PKGS</option>
                      {packageTypes.map((pt) => <option key={pt.id} value={pt.name}>{pt.name}</option>)}
                      {form.total_package_unit && !packageTypes.some((pt) => pt.name === form.total_package_unit) && (
                        <option value={form.total_package_unit}>{form.total_package_unit}</option>
                      )}
                      <option value="__other__">Other...</option>
                    </select>
                  )}
                </div>
                <Field label="Net Weight (Kg)" field="total_net_weight" type="number" placeholder="Auto from line items" readOnly />
              </div>
              <div style={{ ...s.grid3, marginTop: 16 }}>
                <Field label="Gross Weight (Kg)" field="total_gross_weight" type="number" placeholder="Auto from line items" readOnly />
                <Field label="FOB Value (USD)" field="fob_value_usd" type="number" placeholder="Printed as FOB VALUE : US$… under goods" />
                <Field label="Freight Charges (USD)" field="freight_charges_usd" type="number" placeholder="Printed as FREIGHT CHARGES : US$… under goods" />
              </div>
            </div>

            {/* ── Line Items ───────────────────────────────────────────── */}
            <div style={s.card}>
              <div style={s.cardTitle}>
                <div style={s.cardTitleIcon}><FiPackage size={16} /></div>
                Line Items
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={s.lineTable}>
                  <thead>
                    <tr>
                      <th style={{ ...s.lineTh, width: 44, textAlign: 'center' }}>#</th>
                      <th style={{ ...s.lineTh, minWidth: 120 }}>Marks & Nos</th>
                      <th style={{ ...s.lineTh, minWidth: 200 }}>Description of Goods</th>
                      <th style={{ ...s.lineTh, width: 110 }}>H.S. Code</th>
                      <th style={{ ...s.lineTh, width: 90, textAlign: 'right' }}>Qty</th>
                      <th style={{ ...s.lineTh, width: 80 }}>UOM</th>
                      <th style={{ ...s.lineTh, width: 75, textAlign: 'right' }}>Pkgs</th>
                      <th style={{ ...s.lineTh, width: 100 }}>Pkg Type</th>
                      <th style={{ ...s.lineTh, width: 95, textAlign: 'right' }}>Net Wt (Kg)</th>
                      <th style={{ ...s.lineTh, width: 95, textAlign: 'right' }}>Gross Wt (Kg)</th>
                      <th style={{ ...s.lineTh, width: 110, textAlign: 'right' }}>Rate (USD)</th>
                      <th style={{ ...s.lineTh, width: 120, textAlign: 'right' }}>Amount (USD)</th>
                      <th style={{ ...s.lineTh, width: 76, textAlign: 'center' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((item, idx) => (
                      <motion.tr
                        key={item._key}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.15, delay: idx * 0.03 }}
                        style={{ background: idx % 2 === 0 ? '#fff' : '#f8fdfb' }}
                      >
                        <td style={{ ...s.lineTd, textAlign: 'center' }}>
                          <div style={{ color: '#94a3b8', fontWeight: 600, fontSize: 12 }}>{idx + 1}</div>
                          <div style={s.moveBtns}>
                            <button
                              onClick={() => moveLineItem(idx, -1)}
                              disabled={idx === 0}
                              style={{ ...s.moveBtn, opacity: idx === 0 ? 0.25 : 1, cursor: idx === 0 ? 'default' : 'pointer' }}
                              title="Move up"
                              onMouseEnter={(e) => { if (idx !== 0) { e.currentTarget.style.background = '#d1fae5'; e.currentTarget.style.color = '#059669'; } }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#64748b'; }}
                            >
                              <FiChevronUp size={14} />
                            </button>
                            <button
                              onClick={() => moveLineItem(idx, 1)}
                              disabled={idx === lineItems.length - 1}
                              style={{ ...s.moveBtn, opacity: idx === lineItems.length - 1 ? 0.25 : 1, cursor: idx === lineItems.length - 1 ? 'default' : 'pointer' }}
                              title="Move down"
                              onMouseEnter={(e) => { if (idx !== lineItems.length - 1) { e.currentTarget.style.background = '#d1fae5'; e.currentTarget.style.color = '#059669'; } }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#64748b'; }}
                            >
                              <FiChevronDown size={14} />
                            </button>
                          </div>
                        </td>
                        <td style={s.lineTd}>
                          <input
                            value={item.marks_and_nos}
                            onChange={handleLineChange(idx, 'marks_and_nos')}
                            style={s.lineInput}
                            placeholder="Marks"
                            onFocus={onFocusInput}
                            onBlur={onBlurInput}
                          />
                        </td>
                        <td style={s.lineTd}>
                          <textarea
                            value={item.description_of_goods}
                            onChange={handleLineChange(idx, 'description_of_goods')}
                            style={{ ...s.lineInput, resize: 'vertical', minHeight: 32, fontFamily: 'inherit' }}
                            placeholder="Description"
                            rows={2}
                            onFocus={onFocusInput}
                            onBlur={onBlurInput}
                          />
                        </td>
                        <td style={s.lineTd}>
                          <input
                            value={item.hs_code}
                            onChange={handleLineChange(idx, 'hs_code')}
                            style={s.lineInput}
                            placeholder="6109.1000"
                            onFocus={onFocusInput}
                            onBlur={onBlurInput}
                          />
                        </td>
                        <td style={s.lineTd}>
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={handleLineChange(idx, 'quantity')}
                            style={s.lineInputSmall}
                            placeholder="0"
                            onFocus={onFocusInput}
                            onBlur={onBlurInput}
                            onWheel={onWheelNumber}
                          />
                        </td>
                        <td style={s.lineTd}>
                          <input
                            value={item.uom}
                            onChange={handleLineChange(idx, 'uom')}
                            style={s.lineInput}
                            placeholder="PCS"
                            onFocus={onFocusInput}
                            onBlur={onBlurInput}
                          />
                        </td>
                        <td style={s.lineTd}>
                          <input
                            type="number"
                            value={item.no_of_packages}
                            onChange={handleLineChange(idx, 'no_of_packages')}
                            style={s.lineInputSmall}
                            placeholder="0"
                            onFocus={onFocusInput}
                            onBlur={onBlurInput}
                            onWheel={onWheelNumber}
                          />
                        </td>
                        <td style={s.lineTd}>
                          {item.package_type === '__other__' ? (
                            <input
                              type="text"
                              value={item._customPkgType || ''}
                              onChange={(e) => {
                                const val = e.target.value;
                                const updated = [...lineItems];
                                updated[idx] = { ...updated[idx], _customPkgType: val };
                                setLineItems(updated);
                              }}
                              onBlur={(e) => {
                                const val = e.target.value.trim();
                                if (val) {
                                  const updated = [...lineItems];
                                  updated[idx] = { ...updated[idx], package_type: val, _customPkgType: undefined };
                                  setLineItems(updated);
                                }
                              }}
                              placeholder="Type..."
                              style={{ ...s.lineInput, padding: '6px 4px' }}
                              autoFocus
                            />
                          ) : (
                            <select
                              value={packageTypes.some(pt => pt.name === item.package_type) ? item.package_type : (item.package_type && item.package_type !== '' ? '__custom__' : '')}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === '__other__') {
                                  const updated = [...lineItems];
                                  updated[idx] = { ...updated[idx], package_type: '__other__', _customPkgType: '' };
                                  setLineItems(updated);
                                } else {
                                  handleLineChange(idx, 'package_type')(e);
                                }
                              }}
                              style={{ ...s.lineInput, padding: '6px 4px' }}
                              onFocus={onFocusInput}
                              onBlur={onBlurInput}
                            >
                              <option value="">Select</option>
                              {packageTypes.map((pt) => <option key={pt.id} value={pt.name}>{pt.name}</option>)}
                              {item.package_type && !packageTypes.some(pt => pt.name === item.package_type) && item.package_type !== '' && (
                                <option value="__custom__" disabled>{item.package_type}</option>
                              )}
                              <option value="__other__">Other...</option>
                            </select>
                          )}
                        </td>
                        <td style={s.lineTd}>
                          <input
                            type="number"
                            value={item.net_weight_kg}
                            onChange={handleLineChange(idx, 'net_weight_kg')}
                            style={s.lineInputSmall}
                            placeholder="0.00"
                            step="0.001"
                            onFocus={onFocusInput}
                            onBlur={onBlurInput}
                            onWheel={onWheelNumber}
                          />
                        </td>
                        <td style={s.lineTd}>
                          <input
                            type="number"
                            value={item.gross_weight_kg}
                            onChange={handleLineChange(idx, 'gross_weight_kg')}
                            style={s.lineInputSmall}
                            placeholder="0.00"
                            step="0.001"
                            onFocus={onFocusInput}
                            onBlur={onBlurInput}
                            onWheel={onWheelNumber}
                          />
                        </td>
                        <td style={s.lineTd}>
                          <input
                            type="number"
                            value={item.rate_usd}
                            onChange={handleLineChange(idx, 'rate_usd')}
                            style={s.lineInputSmall}
                            placeholder="0.00"
                            step="0.01"
                            onFocus={onFocusInput}
                            onBlur={onBlurInput}
                            onWheel={onWheelNumber}
                          />
                        </td>
                        <td style={s.amountCell}>
                          {fmt(item.amount_usd)}
                        </td>
                        <td style={{ ...s.lineTd, textAlign: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                            <button
                              onClick={() => setExpandedRow(idx)}
                              style={s.expandLine}
                              title="Full view"
                              onMouseEnter={(e) => (e.currentTarget.style.background = '#d1fae5')}
                              onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                            >
                              <FiMaximize2 size={14} />
                            </button>
                            <button
                              onClick={() => removeLineItem(idx)}
                              style={s.removeLine}
                              title="Remove line"
                              onMouseEnter={(e) => (e.currentTarget.style.background = '#fee2e2')}
                              onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                            >
                              <FiTrash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                    <tr style={{ background: '#fbfefd' }}>
                      <td style={{ ...s.lineTd, textAlign: 'center' }}></td>
                      <td colSpan={12} style={s.lineTd}>
                        <textarea
                          value={form.declaration}
                          onChange={handleChange('declaration')}
                          style={{ ...s.lineInput, resize: 'vertical', minHeight: 40, fontFamily: 'inherit' }}
                          placeholder="Additional notes / declaration — printed on the PDF exactly as typed (line breaks kept)"
                          rows={2}
                          onFocus={onFocusInput}
                          onBlur={onBlurInput}
                        />
                      </td>
                    </tr>
                  </tbody>
                  {lineItems.length > 0 && (
                    <tfoot>
                      <tr style={{ background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)', borderTop: '2px solid #059669' }}>
                        <td colSpan={6} style={{ padding: '12px 14px', fontWeight: 700, fontSize: 13, color: '#064e3b', textAlign: 'right', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Totals
                        </td>
                        <td style={{ padding: '12px 8px', fontWeight: 700, fontSize: 13, color: '#064e3b', textAlign: 'center' }}>
                          {lineItems.reduce((s, li) => s + (parseInt(li.no_of_packages) || 0), 0) || '-'}
                        </td>
                        <td></td>
                        <td style={{ padding: '12px 8px', fontWeight: 700, fontSize: 13, color: '#064e3b', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {fmt(lineItems.reduce((s, li) => s + (parseFloat(li.net_weight_kg) || 0), 0))}
                        </td>
                        <td style={{ padding: '12px 8px', fontWeight: 700, fontSize: 13, color: '#064e3b', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {fmt(lineItems.reduce((s, li) => s + (parseFloat(li.gross_weight_kg) || 0), 0))}
                        </td>
                        <td colSpan={2} style={{ padding: '12px 14px', fontWeight: 800, fontSize: 15, color: '#059669', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          ${fmt(form.total_usd)}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

              <motion.button
                whileHover={{ scale: 1.03, borderColor: '#059669' }}
                whileTap={{ scale: 0.97 }}
                onClick={addLineItem}
                style={s.addLineBtn}
              >
                <FiPlus size={15} /> Add Line Item
              </motion.button>

              {/* Totals */}
              <div style={s.totalRow}>
                <div>
                  <div style={s.totalLabel}>Total Invoice (USD)</div>
                  <div style={s.totalValue}>${fmt(form.total_usd)}</div>
                  {form.total_in_words && (
                    <div style={s.totalWords}>{form.total_in_words}</div>
                  )}
                </div>
              </div>

              {/* ── Line Item Full View Modal (portaled so no card/tab stacking context traps it) ── */}
              {createPortal(<AnimatePresence>
                {expandedRow !== null && lineItems[expandedRow] && (() => {
                  const idx = expandedRow;
                  const item = lineItems[idx];
                  return (
                    <motion.div
                      key="line-item-modal"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      style={s.modalOverlay}
                      onClick={() => setExpandedRow(null)}
                    >
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 12 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 12 }}
                        transition={{ duration: 0.18 }}
                        style={s.modalCard}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div style={s.modalHeader}>
                          <div style={s.modalTitle}>
                            <div style={s.cardTitleIcon}><FiPackage size={16} /></div>
                            Line Item {idx + 1} of {lineItems.length}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <button
                              onClick={() => setExpandedRow(idx - 1)}
                              disabled={idx === 0}
                              style={{ ...s.modalNavBtn, opacity: idx === 0 ? 0.35 : 1, cursor: idx === 0 ? 'default' : 'pointer' }}
                              title="Previous item"
                            >
                              <FiChevronUp size={16} />
                            </button>
                            <button
                              onClick={() => setExpandedRow(idx + 1)}
                              disabled={idx === lineItems.length - 1}
                              style={{ ...s.modalNavBtn, opacity: idx === lineItems.length - 1 ? 0.35 : 1, cursor: idx === lineItems.length - 1 ? 'default' : 'pointer' }}
                              title="Next item"
                            >
                              <FiChevronDown size={16} />
                            </button>
                            <button
                              onClick={() => setExpandedRow(null)}
                              style={{ ...s.modalNavBtn, color: '#64748b', background: '#f8fafc', borderColor: '#e2e8f0' }}
                              title="Close"
                            >
                              <FiX size={16} />
                            </button>
                          </div>
                        </div>

                        <div style={{ marginBottom: 14 }}>
                          <label style={s.modalLabel}>Marks & Nos</label>
                          <input value={item.marks_and_nos} onChange={handleLineChange(idx, 'marks_and_nos')} style={s.lineInput} placeholder="Marks" />
                        </div>

                        <div>
                          <label style={s.modalLabel}>Description of Goods</label>
                          <textarea
                            value={item.description_of_goods}
                            onChange={handleLineChange(idx, 'description_of_goods')}
                            style={{ ...s.lineInput, resize: 'vertical', minHeight: 110, fontFamily: 'inherit', fontSize: 14, lineHeight: 1.5 }}
                            placeholder="Full description of goods..."
                            rows={5}
                          />
                        </div>

                        <div style={s.modalGrid}>
                          <div>
                            <label style={s.modalLabel}>H.S. Code</label>
                            <input value={item.hs_code} onChange={handleLineChange(idx, 'hs_code')} style={s.lineInput} placeholder="6109.1000" />
                          </div>
                          <div>
                            <label style={s.modalLabel}>Quantity</label>
                            <input type="number" value={item.quantity} onChange={handleLineChange(idx, 'quantity')} style={s.lineInputSmall} placeholder="0" onWheel={onWheelNumber} />
                          </div>
                          <div>
                            <label style={s.modalLabel}>UOM</label>
                            <input value={item.uom} onChange={handleLineChange(idx, 'uom')} style={s.lineInput} placeholder="PCS" />
                          </div>
                          <div>
                            <label style={s.modalLabel}>No. of Packages</label>
                            <input type="number" value={item.no_of_packages} onChange={handleLineChange(idx, 'no_of_packages')} style={s.lineInputSmall} placeholder="0" onWheel={onWheelNumber} />
                          </div>
                          <div>
                            <label style={s.modalLabel}>Package Type</label>
                            {item.package_type === '__other__' ? (
                              <input
                                type="text"
                                value={item._customPkgType || ''}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  const updated = [...lineItems];
                                  updated[idx] = { ...updated[idx], _customPkgType: val };
                                  setLineItems(updated);
                                }}
                                onBlur={(e) => {
                                  const val = e.target.value.trim();
                                  if (val) {
                                    const updated = [...lineItems];
                                    updated[idx] = { ...updated[idx], package_type: val, _customPkgType: undefined };
                                    setLineItems(updated);
                                  }
                                }}
                                placeholder="Type..."
                                style={s.lineInput}
                                autoFocus
                              />
                            ) : (
                              <select
                                value={packageTypes.some(pt => pt.name === item.package_type) ? item.package_type : (item.package_type && item.package_type !== '' ? '__custom__' : '')}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (val === '__other__') {
                                    const updated = [...lineItems];
                                    updated[idx] = { ...updated[idx], package_type: '__other__', _customPkgType: '' };
                                    setLineItems(updated);
                                  } else {
                                    handleLineChange(idx, 'package_type')(e);
                                  }
                                }}
                                style={s.lineInput}
                              >
                                <option value="">Select</option>
                                {packageTypes.map((pt) => <option key={pt.id} value={pt.name}>{pt.name}</option>)}
                                {item.package_type && !packageTypes.some(pt => pt.name === item.package_type) && item.package_type !== '' && (
                                  <option value="__custom__" disabled>{item.package_type}</option>
                                )}
                                <option value="__other__">Other...</option>
                              </select>
                            )}
                          </div>
                          <div>
                            <label style={s.modalLabel}>Net Wt (Kg)</label>
                            <input type="number" value={item.net_weight_kg} onChange={handleLineChange(idx, 'net_weight_kg')} style={s.lineInputSmall} placeholder="0.00" step="0.001" onWheel={onWheelNumber} />
                          </div>
                          <div>
                            <label style={s.modalLabel}>Gross Wt (Kg)</label>
                            <input type="number" value={item.gross_weight_kg} onChange={handleLineChange(idx, 'gross_weight_kg')} style={s.lineInputSmall} placeholder="0.00" step="0.001" onWheel={onWheelNumber} />
                          </div>
                          <div>
                            <label style={s.modalLabel}>Rate (USD)</label>
                            <input type="number" value={item.rate_usd} onChange={handleLineChange(idx, 'rate_usd')} style={s.lineInputSmall} placeholder="0.00" step="0.01" onWheel={onWheelNumber} />
                          </div>
                        </div>

                        <div style={s.modalFooter}>
                          <div>
                            <div style={s.modalLabel}>Amount (USD)</div>
                            <div style={{ fontSize: 22, fontWeight: 800, color: '#059669', fontVariantNumeric: 'tabular-nums' }}>
                              ${fmt(item.amount_usd) || '0.00'}
                            </div>
                          </div>
                          <button onClick={() => setExpandedRow(null)} style={s.modalDoneBtn}>
                            <FiCheckCircle size={15} /> Done
                          </button>
                        </div>
                      </motion.div>
                    </motion.div>
                  );
                })()}
              </AnimatePresence>, document.body)}
            </div>

            {/* ── Other Reference ──────────────────────────────────── */}
            <div style={s.card}>
              <div style={s.cardTitle}>
                <div style={s.cardTitleIcon}><FiHash size={16} /></div>
                Other Reference
              </div>
              <Field label="Other Reference" field="other_ref" placeholder="Any other reference (printed on the PDF exactly as typed, line breaks kept)" rows={5} />
              <Field label="Other Reference 2" field="other_ref_2" placeholder="Importer registration line - printed on every document after the proforma reference, exactly as typed (e.g. IRC NO...., BIN NO...., TIN NO....)" rows={3} />
            </div>

            {/* ── TR / BOE / Despatch ──────────────────────────────── */}

              <div style={s.card}>
                <div style={s.cardTitle}>
                  <div style={s.cardTitleIcon}><FiAnchor size={16} /></div>
                  TR / BOE / Despatch Details
                </div>
                <div style={s.grid3}>
                  <Field label="Despatch Doc No" field="despatch_doc_no" placeholder="Document no." />
                  <Field label="Despatch Date" field="despatch_date" type="date" />
                  <Field label="Carrier" field="carrier" placeholder="e.g. By Road" />
                </div>
                <div style={{ ...s.grid3, marginTop: 16 }}>
                  <Field label="Vehicle No" field="vehicle_no" placeholder="Vehicle/Vessel" />
                  <Field label="S.B. No" field="sb_no" placeholder="Shipping bill no." />
                  <Field label="S.B. Date" field="sb_date" type="date" />
                </div>
                <div style={{ ...s.grid3, marginTop: 16 }}>
                  <Field label="Export Date" field="export_date" type="date" />
                  <Field label="To the Order of" field="to_the_order_of" placeholder="Consignee bank" />
                </div>
                <div style={{ marginTop: 16 }}>
                  <Field label="Notify" field="notify" placeholder="Notify party" rows={3} />
                </div>
                <div style={{ marginTop: 16 }}>
                  <Field label="Drafts Drawn" field="drafts_drawn" placeholder="Draft details" rows={2} />
                </div>
              </div>

            {/* ── Post-Shipment / Realization ─────────────────────── */}
            <div style={s.card}>
              <div style={s.cardTitle}>
                <div style={s.cardTitleIcon}><FiEdit3 size={16} /></div>
                Post-Shipment / Realization
              </div>
              <div style={s.grid3}>
                <Field label="Commodity" field="commodity" placeholder="Commodity type" />
                <Field label="Bill Ref" field="bill_ref" placeholder="Bill reference" />
                <Field label="Due Date" field="due_date" type="date" />
              </div>
              <div style={{ ...s.grid3, marginTop: 16 }}>
                <Field label="Payment Date" field="payment_date" type="date" />
                <Field label="FOB Value (INR)" field="fob_inr" type="number" placeholder="0.00" />
                <Field label="Drawback (INR)" field="drawback" type="number" placeholder="0.00" />
              </div>
            </div>
          </motion.div>
        )}

        {/* ════════════════════════════════════════════════════════════════ */}
        {/* TAB 2: IEC DISPLAY                                             */}
        {/* ════════════════════════════════════════════════════════════════ */}
        {activeTab === 'iec' && (
          <motion.div
            key="iec"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.25 }}
          >
            <div style={s.card}>
              <div style={s.cardTitle}>
                <div style={s.cardTitleIcon}><FiCreditCard size={16} /></div>
                Importer-Exporter Code (IEC) Details
                <span style={{ fontSize: 11, fontWeight: 500, color: '#94a3b8', marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <FiLock size={11} /> Read-only from company profile
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {/* IEC Number */}
                <div style={s.iecField}>
                  <div style={s.iecLabel}>IEC Number</div>
                  <div style={s.iecValue}>{companyData?.iec || companyData?.iec_number || form.iec || 'N/A'}</div>
                </div>

                {/* Company Name */}
                <div style={s.iecField}>
                  <div style={s.iecLabel}>Exporter Name</div>
                  <div style={s.iecValue}>{companyData?.exporter_name || form.exporter_name || 'N/A'}</div>
                </div>

                {/* Company Address */}
                <div style={{ ...s.iecField, gridColumn: 'span 2' }}>
                  <div style={s.iecLabel}>Registered Address</div>
                  <div style={s.iecValue}>
                    {companyData?.exporter_address || companyData?.address || form.exporter_address || 'N/A'}
                  </div>
                </div>

                {/* PAN */}
                <div style={s.iecField}>
                  <div style={s.iecLabel}>PAN</div>
                  <div style={s.iecValue}>{companyData?.pan || companyData?.pan_number || 'N/A'}</div>
                </div>

                {/* GSTIN */}
                <div style={s.iecField}>
                  <div style={s.iecLabel}>GSTIN</div>
                  <div style={s.iecValue}>{companyData?.gstin || companyData?.gst_number || 'N/A'}</div>
                </div>

                {/* State */}
                <div style={s.iecField}>
                  <div style={s.iecLabel}>State</div>
                  <div style={s.iecValue}>{companyData?.state || 'N/A'}</div>
                </div>

                {/* Country */}
                <div style={s.iecField}>
                  <div style={s.iecLabel}>Country</div>
                  <div style={s.iecValue}>{companyData?.country || 'India'}</div>
                </div>

                {/* Email */}
                <div style={s.iecField}>
                  <div style={s.iecLabel}>Email</div>
                  <div style={s.iecValue}>{companyData?.email || 'N/A'}</div>
                </div>

                {/* Phone */}
                <div style={s.iecField}>
                  <div style={s.iecLabel}>Phone</div>
                  <div style={s.iecValue}>{companyData?.phone || companyData?.contact_number || 'N/A'}</div>
                </div>

                {/* Bank Details */}
                <div style={{ ...s.iecField, gridColumn: 'span 2' }}>
                  <div style={s.iecLabel}>Bank Details</div>
                  <div style={s.iecValue}>
                    {companyData?.bank_name ? (
                      <>
                        {companyData.bank_name}
                        {companyData.bank_branch && `, ${companyData.bank_branch}`}
                        {companyData.bank_account_no && <><br />A/C: {companyData.bank_account_no}</>}
                        {companyData.bank_ifsc && <><br />IFSC: {companyData.bank_ifsc}</>}
                        {companyData.bank_swift && <><br />SWIFT: {companyData.bank_swift}</>}
                        {companyData.bank_ad_code && <><br />AD Code: {companyData.bank_ad_code}</>}
                      </>
                    ) : 'N/A'}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* ════════════════════════════════════════════════════════════════ */}
        {/* TAB 4: ATTACHMENTS                                             */}
        {/* ════════════════════════════════════════════════════════════════ */}
        {activeTab === 'attachments' && isEdit && (
          <motion.div
            key="attachments"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.25 }}
          >
            <div style={s.card}>
              <div style={s.cardTitle}>
                <div style={s.cardTitleIcon}><FiPaperclip size={16} /></div>
                Document Attachments
                <span style={{ fontSize: 12, fontWeight: 500, color: '#6b7280', marginLeft: 'auto' }}>
                  {attachments.length} file{attachments.length !== 1 ? 's' : ''}
                </span>
              </div>

              {/* ── Drag & Drop Zone ───────────────────────────────────── */}
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: `2px dashed ${dragOver ? '#10b981' : 'rgba(16,185,129,0.25)'}`,
                  borderRadius: 16,
                  padding: '40px 20px',
                  textAlign: 'center',
                  cursor: uploading ? 'wait' : 'pointer',
                  background: dragOver ? 'rgba(16,185,129,0.06)' : '#fafffe',
                  transition: 'all 0.2s ease',
                  marginBottom: 24,
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    handleFileUpload(e.target.files);
                    e.target.value = '';
                  }}
                />
                {uploading ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                    <div style={s.spinner} />
                    <span style={{ color: '#6b7280', fontSize: 14 }}>Uploading files...</span>
                  </div>
                ) : (
                  <>
                    <FiUploadCloud size={40} color={dragOver ? '#10b981' : '#94a3b8'} style={{ marginBottom: 12 }} />
                    <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#374151' }}>
                      Drag & drop files here
                    </p>
                    <p style={{ margin: '6px 0 0', fontSize: 13, color: '#9ca3af' }}>
                      or <span style={{ color: '#059669', fontWeight: 600, textDecoration: 'underline' }}>click to browse</span>
                    </p>
                  </>
                )}
              </div>

              {/* ── Attachment List ─────────────────────────────────────── */}
              {attachments.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px 20px', color: '#9ca3af' }}>
                  <FiFile size={36} style={{ marginBottom: 10, color: 'rgba(16,185,129,0.25)' }} />
                  <p style={{ margin: 0, fontSize: 14, color: '#9ca3af' }}>No files attached yet</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {attachments.map((att) => {
                    const FileIcon = getFileIcon(att.mime_type);
                    return (
                      <motion.div
                        key={att.id}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 14,
                          padding: '12px 16px',
                          background: '#f8fdfb',
                          borderRadius: 12,
                          border: '1px solid rgba(16,185,129,0.1)',
                        }}
                      >
                        <div style={{
                          width: 38, height: 38, borderRadius: 10,
                          background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0,
                        }}>
                          <FileIcon size={18} color="#059669" />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontSize: 14, fontWeight: 600, color: '#1e293b',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {att.file_name}
                          </div>
                          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                            {formatFileSize(att.file_size)}
                            <span style={{ margin: '0 6px' }}>·</span>
                            {new Date(att.uploaded_at).toLocaleDateString('en-IN', {
                              day: '2-digit', month: 'short', year: 'numeric',
                            })}
                          </div>
                        </div>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleDownloadAttachment(att)}
                          title="Download"
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: '#059669', padding: 8, borderRadius: 8,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = '#ecfdf5')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                        >
                          <FiDownload size={17} />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleDeleteAttachment(att)}
                          title="Delete"
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: '#ef4444', padding: 8, borderRadius: 8,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = '#fee2e2')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                        >
                          <FiTrash2 size={17} />
                        </motion.button>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Action Buttons ──────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        style={s.actions}
      >
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => navigate('/commercial-invoices')}
          style={s.btnSecondary}
          disabled={saving}
        >
          <FiX size={16} /> Close
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => handleSave(true)}
          style={s.btnDraft}
          disabled={saving}
        >
          {saving ? (
            <div style={{ width: 18, height: 18, border: '2px solid rgba(120,53,15,0.3)', borderTopColor: '#78350f', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          ) : (
            <FiEdit3 size={16} />
          )}
          Save as Draft
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => handleSave(false)}
          style={s.btnPrimary}
          disabled={saving}
        >
          {saving ? (
            <div style={{ width: 18, height: 18, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          ) : (
            <FiSave size={16} />
          )}
          {isEdit ? 'Update Document' : 'Save Document'}
        </motion.button>
      </motion.div>
    </motion.div>
    </FormFieldContext.Provider>
  );
}
